import { Alert } from '../../alerts/models/Alert';
import { DeviceId } from '../models/Device';
import {
    DeviceInfo,
    DeviceProperty,
    EditableSettingsProperty,
    PropertyId,
    ResolvedSection,
} from '../models/DeviceDetail';
import { DeviceDiagnosticSectionVm } from '../models/DeviceDiagnosticPropertyVm';
import { DeviceSettingsSectionVm } from '../models/DeviceSettingsPropertyVm';
import { DeviceStateUpdateExecutionInfo, ExecutionInfo } from '../models/Execution';
import { SECTION_REFRESH_STATUS } from '../models/SectionRefreshVm';
import { SECTION_TYPES } from '../models/SectionType';
import { UiProfile } from '../models/UiProfile';

import { buildDiagnosticSection } from './build-diagnostic';
import { buildSettingSection } from './build-settings';
import { filterAlertsDevice } from './device-alert';
import { computeSectionRefreshVm } from './device-refresh';

export const SECTIONS_EXCLUDED_FROM_DIAGNOSTIC = new Set<string>([SECTION_TYPES.SETTINGS, SECTION_TYPES.PRESENTATION]);

export interface DeviceContext {
    deviceId: DeviceId;
    device: DeviceInfo;
    uiProfile: UiProfile;
}

export interface DeviceDiagnosticSectionsContext {
    ctx: DeviceContext | null;
    uiProfile: UiProfile | null;
    properties: Record<PropertyId, DeviceProperty> | undefined;
    sections: ResolvedSection[] | undefined;
    sectionRefreshTasks: ExecutionInfo[] | undefined;
}

export interface DeviceSettingsSectionContext {
    ctx: DeviceContext | null;
    editableProperties: EditableSettingsProperty[] | undefined;
    isInIntervention: boolean;
    executionInfos: DeviceStateUpdateExecutionInfo[] | undefined;
    settingsSections: ResolvedSection[] | undefined;
    sectionRefreshTasks: ExecutionInfo[] | undefined;
}

export function buildDeviceDiagnosticSectionsVm(
    context: DeviceDiagnosticSectionsContext | undefined,
    siteAlerts: readonly Alert[],
    gatewayAvailable: boolean,
): DeviceDiagnosticSectionVm[] {
    if (!context) return [];
    const { ctx, uiProfile, properties, sections, sectionRefreshTasks } = context;
    if (!ctx || !uiProfile || !properties || !sections) return [];

    const deviceAlerts = filterAlertsDevice(ctx.deviceId, [...siteAlerts]);
    return sections
        .filter((section) => !SECTIONS_EXCLUDED_FROM_DIAGNOSTIC.has(section.title))
        .map((section) => {
            const diagnosticSection = buildDiagnosticSection(
                section,
                uiProfile,
                properties,
                ctx.device.lastUpdateTime,
                deviceAlerts,
            );
            const uiSection = uiProfile.sections.find((uiProfileSection) => uiProfileSection.title === section.title);
            const refreshVm = uiSection
                ? computeSectionRefreshVm(ctx.device, uiSection, gatewayAvailable, sectionRefreshTasks)
                : { status: SECTION_REFRESH_STATUS.NONE };

            return {
                ...diagnosticSection,
                refreshVisible: refreshVm.status !== SECTION_REFRESH_STATUS.NONE,
                refreshing: refreshVm.status === SECTION_REFRESH_STATUS.IN_PROGRESS,
                refreshDisabled: refreshVm.status === SECTION_REFRESH_STATUS.DISABLED,
                refreshTooltip: refreshVm.tooltip ?? '',
            };
        })
        .filter((section) => section.properties.length > 0);
}

export function buildDeviceSettingsSectionVm(
    context: DeviceSettingsSectionContext | undefined,
    siteAlerts: readonly Alert[],
    gatewayAvailable: boolean,
): DeviceSettingsSectionVm {
    if (!context?.ctx || !context.editableProperties) {
        return { properties: [], refreshVisible: false, refreshing: false, refreshDisabled: false, refreshTooltip: '' };
    }

    const { ctx, editableProperties, isInIntervention, executionInfos, settingsSections, sectionRefreshTasks } =
        context;
    const deviceAlerts = filterAlertsDevice(ctx.deviceId, [...siteAlerts]);
    const properties = editableProperties.map(({ propertyId, uiProperty, deviceProperty }) =>
        buildSettingSection(
            propertyId,
            uiProperty,
            deviceProperty,
            executionInfos,
            ctx.deviceId,
            ctx.device,
            gatewayAvailable,
            isInIntervention,
            deviceAlerts,
        ),
    );
    const uiSection = ctx.uiProfile.sections.find((section) => section.title === SECTION_TYPES.SETTINGS);
    const refreshVm =
        uiSection && settingsSections?.length
            ? computeSectionRefreshVm(ctx.device, uiSection, gatewayAvailable, sectionRefreshTasks)
            : { status: SECTION_REFRESH_STATUS.NONE };

    return {
        properties,
        refreshVisible: refreshVm.status !== SECTION_REFRESH_STATUS.NONE,
        refreshing: refreshVm.status === SECTION_REFRESH_STATUS.IN_PROGRESS,
        refreshDisabled: refreshVm.status === SECTION_REFRESH_STATUS.DISABLED,
        refreshTooltip: refreshVm.tooltip ?? '',
    };
}
